import React from 'react';
import { formatIn } from '../../../function-utils/building-time';
import {
  EmptyState,
  ReportTable,
  ReportTableWrapper,
} from './view-daily-report.styled';
import {
  formatString,
  stripHtmlTags,
} from '../../../function-utils/string-converter';

/**
 * BELT-AND-BRACES. A non-null, non-array object rendered as a React child
 * throws, and with no error boundary anywhere in this app the throw unmounts
 * the whole root — the blank "Read the log" screen. A column that holds one
 * anywhere on the page is dropped rather than rendered. This HIDES the column
 * on purpose; the real fix is the server-side allow-list in serializeLog.
 */
const isObjectValue = (value: unknown) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

interface ReportsTableProps {
  // eslint-disable-next-line
  log: any[];
  /** The zone every row's time is printed in — the BUILDING's, on this desk
   *  screen. It was the device's, whatever zone the tablet was set to. Absent
   *  only in a test that is not about time; the one time module then falls back
   *  to the device's clock. */
  tz?: string;
}

export const ReportsTable: React.FC<ReportsTableProps> = ({ log, tz }) => {
  // British date, 24-hour clock (the product locale), in the zone it is handed.
  const formatDateTime = (isoString: string) =>
    formatIn(isoString, tz, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  const headers =
    log?.length > 0
      ? Object.keys(log[0])
          .filter(
            header =>
              header !== 'userId' &&
              header !== 'id' &&
              header !== 'updatedAt' &&
              header !== 'property_ids' &&
              log.some(item => item[header]) &&
              !log.some(item => isObjectValue(item[header]))
          )
          .map(header => header)
      : [];

  if (!log?.length) {
    return <EmptyState>No entries logged for this report yet.</EmptyState>;
  }

  return (
    <ReportTableWrapper>
      <ReportTable>
        <thead>
          <tr>
            {headers?.map(header => (
              <th key={header}>{formatString(header)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {log?.map((item, rowIndex) => (
            <tr key={rowIndex}>
              {headers?.map(header => {
                // Format 'createdAt' header's value
                const cellValue =
                  header === 'createdAt'
                    ? formatDateTime(item[header])
                    : stripHtmlTags(item[header]);

                return (
                  <td key={`${rowIndex}-${header}`}>
                    {header === 'names' || header === 'lastNames'
                      ? item?.[header]?.map((value: string, index: number) => (
                          <div key={index}>{value}</div>
                        ))
                      : cellValue}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </ReportTable>
    </ReportTableWrapper>
  );
};
